import React, { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/state/store';
import SimpleLoadingScreen from '@/components/SimpleLoadingScreen';

type RequireAuthProps = {
  children?: React.ReactNode;
};

export default function RequireAuth(props: RequireAuthProps): JSX.Element {
  const { isAuthenticated, isLoading, openBankingOnly, openBankingOnlyUserId, user } = useAuth();
  const location = useLocation();

  // Still loading authentication state → show loading
  if (isLoading) {
    return <SimpleLoadingScreen title="Loading..." />;
  }

  // Not authenticated → redirect to auth
  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location.pathname + location.search }} replace />;
  }

  // Open Banking-only lock: prevent navigation to any other authenticated route.
  if (
    openBankingOnly &&
    typeof user?.userId === 'number' &&
    (openBankingOnlyUserId === null || openBankingOnlyUserId === user.userId)
  ) {
    const allowed = new Set<string>(['/open-banking', '/ob-confirm-transaction', '/obq', '/ob-summary', '/ob-thank-you']);
    if (!allowed.has(location.pathname)) {
      return <Navigate to="/open-banking" replace />;
    }
  }

  // UW customer lock: prevent UW customers from accessing non-UW pages.
  // UW customers should only access /uw/* routes. Redirect others to / (RootRoute handles UW routing).
  const isUwCustomer = useMemo(() => {
    try {
      const raw = localStorage.getItem('customerData');
      if (!raw) return false;
      const data = JSON.parse(raw);
      return data?.isUwCustomer === true;
    } catch {
      return false;
    }
  }, [location.pathname]);

  if (isUwCustomer && !location.pathname.startsWith('/uw')) {
    return <Navigate to="/" replace />;
  }

  // Authenticated → render protected content
  return (props.children ? <>{props.children}</> : <Outlet />);
}